/* eslint-disable jsx-a11y/anchor-is-valid */
// src/components/Navbar.jsx - FINAL FIX
import React, { useState, useEffect, useRef, useContext } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { API_URL, getPlaceholderImage } from '../config';
import { useCart } from "../components/CartContext";
import { useFloatingCart } from "../components/FloatingCartContext";
import { UserContext } from "../components/UserContext";
import { useWishlist } from "../components/WishlistContext";
import { FaSearch, FaShoppingCart, FaUser, FaSignOutAlt, FaSignInAlt, FaPhone, FaUserPlus, FaBars, FaTimes, FaRegHeart } from "react-icons/fa";
import { trackPhoneClick } from "../utils/callTracking";
import "./Navbar.css";

const Navbar = () => {
  const { cart = [] } = useCart();
  const { openCart } = useFloatingCart();
  const { user, logout } = useContext(UserContext);
  const { wishlistCount } = useWishlist();
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1); // For keyboard navigation
  const [cartAnimate, setCartAnimate] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [creditInfo, setCreditInfo] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [logoHover, setLogoHover] = useState(false);

  // Animated search placeholder states
  const [placeholder, setPlaceholder] = useState("Search for ");
  const [typingIndex, setTypingIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTermIndex, setSearchTermIndex] = useState(0);
  const [categories, setCategories] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef(null);

  // Detect dark mode preference
  useEffect(() => {
    const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkMode(darkModeQuery.matches);

    const handleChange = (e) => setIsDarkMode(e.matches);
    darkModeQuery.addEventListener('change', handleChange);
    return () => darkModeQuery.removeEventListener('change', handleChange);
  }, []);

  // Fetch categories for animated placeholder
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/products/categories`);
        if (response.ok) {
          const data = await response.json();
          // Convert category names to lowercase for search terms
          // Handle both string (legacy) and object (new hierarchy) formats
          const searchTerms = (data.categories || []).map(cat => {
            const catName = typeof cat === 'string' ? cat : cat.name;
            return catName.toLowerCase();
          });
          setCategories(searchTerms.length > 0 ? searchTerms : getDefaultCategories());
        } else {
          setCategories(getDefaultCategories());
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategories(getDefaultCategories());
      }
    };

    const getDefaultCategories = () => [
      "access & inspection",
      "accessories",
      "dampers",
      "ducting",
      "fans",
      "fire protection",
      "fittings",
      "mounting & support",
      "vents"
    ];

    fetchCategories();
  }, []);

  // Use centralized API URL from config (includes /api)

  const totalItems = cart.reduce((sum, item) => sum + (item.quantity || 1), 0);
  const totalPrice = cart.reduce((sum, item) => sum + (item.quantity || 1) * Number(item.price), 0);

  // Fetch credit account info for logged-in users
  useEffect(() => {
    const fetchCreditInfo = async () => {
      if (!user) {
        setCreditInfo(null);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const response = await fetch(`${API_URL}/credit-account/info`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        // Handle authentication failures gracefully (no console spam)
        if (response.status === 401 || response.status === 403) {
          setCreditInfo(null);
          return;
        }

        if (response.ok) {
          const data = await response.json();
          if (data.hasTradeAccount) {
            setCreditInfo(data);
          } else {
            setCreditInfo(null);
          }
        } else {
          // Handle other errors silently
          setCreditInfo(null);
        }
      } catch (error) {
        // Silently fail for network errors (token might be expired)
        setCreditInfo(null);
      }
    };

    fetchCreditInfo();
  }, [user]);

  // Animate cart when items change
  useEffect(() => {
    if (totalItems > 0) {
      setCartAnimate(true);
      const timeout = setTimeout(() => setCartAnimate(false), 600);
      return () => clearTimeout(timeout);
    }
  }, [totalItems]);

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchFocused(false);
        setResults([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);


  // Prevent body scroll and manage body class when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      // Scroll page to top when menu opens
      window.scrollTo(0, 0);
      document.body.classList.add('mobile-menu-is-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('mobile-menu-is-open');
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.classList.remove('mobile-menu-is-open');
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  // Animated placeholder typing effect
  useEffect(() => {
    // Don't animate if search is focused, has content, or no categories loaded
    if (isSearchFocused || searchTerm || categories.length === 0) {
      return;
    }

    const currentTerm = categories[searchTermIndex];
    const typingSpeed = isDeleting ? 50 : 80;
    const pauseTime = 2000;

    const timeout = setTimeout(() => {
      if (!isDeleting && typingIndex < currentTerm.length) {
        // Typing forward
        setPlaceholder("Search for " + currentTerm.substring(0, typingIndex + 1));
        setTypingIndex(typingIndex + 1);
      } else if (!isDeleting && typingIndex === currentTerm.length) {
        // Pause at end before deleting
        setTimeout(() => setIsDeleting(true), pauseTime);
      } else if (isDeleting && typingIndex > 0) {
        // Deleting backward
        setPlaceholder("Search for " + currentTerm.substring(0, typingIndex - 1));
        setTypingIndex(typingIndex - 1);
      } else if (isDeleting && typingIndex === 0) {
        // Move to next term
        setIsDeleting(false);
        setSearchTermIndex((searchTermIndex + 1) % categories.length);
      }
    }, typingSpeed);

    return () => clearTimeout(timeout);
  }, [typingIndex, isDeleting, searchTermIndex, isSearchFocused, searchTerm, categories]);

  // Search products from MongoDB
  const onChange = async (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setSelectedIndex(-1); // Reset selection when typing

    if (!term.trim()) {
      setResults([]);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/products?search=${encodeURIComponent(term)}&status=active&limit=10`);
      if (response.ok) {
        const data = await response.json();
        setResults(data.products || []);
      }
    } catch (error) {
      console.error('Search failed:', error);
      setResults([]);
    }
  };

  const openProduct = (id) => {
    setSearchTerm("");
    setResults([]);
    setSelectedIndex(-1);
    setIsSearchFocused(false);
    navigate(`/product/${id}`);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (results.length > 0) {
      // If an item is selected via keyboard, open that one; otherwise open first result
      const indexToOpen = selectedIndex >= 0 ? selectedIndex : 0;
      openProduct(results[indexToOpen]._id);
    }
  };

  // Keyboard navigation handler
  const handleKeyDown = (e) => {
    if (!isSearchFocused || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < results.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Escape':
        e.preventDefault();
        setIsSearchFocused(false);
        setSelectedIndex(-1);
        setResults([]);
        break;
      case 'Enter':
        if (selectedIndex >= 0) {
          e.preventDefault();
          openProduct(results[selectedIndex]._id);
        }
        break;
      default:
        break;
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Function to handle phone icon click
  const handlePhoneClick = (e) => {
    e.preventDefault(); // Prevent default link behavior
    trackPhoneClick(); // Track the call for ad performance
    alert("Please call us at 01482 205358 to place your order or for assistance.");
  };

  // Check if current path is active
  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <nav>
      {/* Top Bar */}
      <div className="navbar-top">
        <div className="navbar-left">
          <Link
            to="/"
            className="navbar-logo"
            aria-label="TR Ventilation Home"
            onMouseEnter={() => setLogoHover(true)}
            onMouseLeave={() => setLogoHover(false)}
          >
            <img
              src={isDarkMode ? "/TRVentilation_logo_dark.png" : "/TRVentilation_logo.png"}
              alt="TR Ventilation - For all your ductwork needs"
              className={`navbar-logo-img ${logoHover ? 'logo-hover' : ''}`}
            />
          </Link>

          <a 
            href="tel:01482205358" 
            className="navbar-phone"
            aria-label="Call us at 01482 205358"
            onClick={handlePhoneClick} // Add click handler
          >
            <FaPhone />
            <span className="phone-number-text">01482 205358</span> {/* Wrap text in span */}
          </a>
        </div>

        <div className="navbar-right">
          {/* Search */}
          <form 
            ref={searchRef} 
            className="navbar-search-form" 
            onSubmit={onSubmit}
          >
            <input
              type="text"
              className="navbar-search-input"
              placeholder={placeholder}
              value={searchTerm}
              onChange={onChange}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => {
                setIsSearchFocused(false);
                if (!searchTerm) {
                  // Reset to "Search for " when user leaves empty search
                  setTypingIndex(0);
                  setIsDeleting(false);
                  setPlaceholder("Search for ");
                }
              }}
              aria-label="Search products"
              aria-autocomplete="list"
              aria-controls="search-results"
            />
            <button 
              type="submit" 
              className="navbar-search-btn"
              aria-label="Search"
            >
              <FaSearch />
            </button>

            {isSearchFocused && results.length > 0 && (
              <div className="navbar-search-dropdown" role="listbox" id="search-results">
                {results.map((p, index) => (
                  <div
                    key={p._id}
                    className={`navbar-search-item ${selectedIndex === index ? 'selected' : ''}`}
                    onMouseDown={(e) => {
                      // Use onMouseDown instead of onClick to fire BEFORE onBlur
                      // This prevents the input blur from closing the dropdown first
                      e.preventDefault();
                      e.stopPropagation();
                      openProduct(p._id);
                    }}
                    role="option"
                    aria-selected={selectedIndex === index}
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') openProduct(p._id);
                    }}
                  >
                    <img
                      src={p.image || getPlaceholderImage()}
                      alt={p.name}
                      loading="lazy"
                    />
                    <div className="navbar-search-item-text">
                      <div className="name">{p.name}</div>
                      <div className="desc">{p.shortDescription}</div>
                    </div>
                    <div className="price">£{Number(p.price).toFixed(2)}</div>
                  </div>
                ))}
              </div>
            )}
          </form>

          {/* Wishlist */}
          <Link
            to="/wishlist"
            className="navbar-wishlist"
            aria-label={`Wishlist with ${wishlistCount} items`}
          >
            <FaRegHeart />
            <span className="wishlist-badge">{wishlistCount}</span>
          </Link>

          {/* Cart */}
          <button
            onClick={openCart}
            className={`navbar-cart ${cartAnimate ? "cart-bounce" : ""}`}
            aria-label={`Shopping cart with ${totalItems} items`}
          >
            <FaShoppingCart />
            <span>{totalItems} {totalItems === 1 ? 'item' : 'items'}</span>
            <span>£{totalPrice.toFixed(2)}</span>
          </button>

          {/* Hamburger Menu Button */}
          <button
            className="navbar-hamburger"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle mobile menu"
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>

      {/* Bottom Bar - Navigation */}
      <div className={`navbar-bottom ${mobileMenuOpen ? 'mobile-menu-open' : ''}`}>
        <button
          className="navbar-mobile-close"
          onClick={() => setMobileMenuOpen(false)}
          aria-label="Close mobile menu"
        >
          <FaTimes />
        </button>
        <div className="navbar-links">
          <Link 
            to="/products"
            className={isActive('/products') ? 'active' : ''}
            aria-current={isActive('/products') ? 'page' : undefined}
          >
            Products
          </Link>
          <Link 
            to="/about"
            className={isActive('/about') ? 'active' : ''}
            aria-current={isActive('/about') ? 'page' : undefined}
          >
            About
          </Link>
          <Link
            to="/contact"
            className={isActive('/contact') ? 'active' : ''}
            aria-current={isActive('/contact') ? 'page' : undefined}
          >
            Contact
          </Link>
          {user && (
            <Link
              to="/register-trade"
              className={isActive('/register-trade') ? 'active' : ''}
              aria-current={isActive('/register-trade') ? 'page' : undefined}
            >
              Trade Accounts
            </Link>
          )}
          {user?.tradeAccount?.status === 'approved' && (
            <Link
              to="/b2b-resources"
              className={isActive('/b2b-resources') ? 'active' : ''}
              aria-current={isActive('/b2b-resources') ? 'page' : undefined}
            >
              B2B Resources
            </Link>
          )}
        </div>

        <div className="navbar-account-links">
          {user ? (
            <>
              <Link
                to="/account"
                className={`navbar-account-link ${isActive('/account') ? 'active' : ''}`}
                aria-current={isActive('/account') ? 'page' : undefined}
              >
                {user.profilePicture ? (
                  <img
                    src={user.profilePicture}
                    alt="Profile"
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      marginRight: "8px"
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      backgroundColor: "#003366",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      fontWeight: "bold",
                      marginRight: "8px"
                    }}
                  >
                    {user.name
                      ?.split(' ')
                      .filter(word => word.length > 0)
                      .map(word => word.replace(/[^a-zA-Z0-9]/g, '')[0])
                      .filter(letter => letter)
                      .slice(0, 2)
                      .join('')
                      .toUpperCase() || "?"}
                  </div>
                )}
                <span className="navbar-username">{user.name}</span>
              </Link>
              {user.role === 'admin' && (
                <Link
                  to="/admin"
                  className={`navbar-admin-link ${isActive('/admin') ? 'active' : ''}`}
                  aria-current={isActive('/admin') ? 'page' : undefined}
                >
                  🛠️ Admin Panel
                </Link>
              )}
              {creditInfo && (
                <div
                  className="navbar-credit-badge"
                  title={`Credit Limit: £${creditInfo.creditLimit?.toFixed(2) || '0.00'} | Used: £${creditInfo.creditUsed?.toFixed(2) || '0.00'}`}
                >
                  💳 £{creditInfo.availableCredit?.toFixed(2) || '0.00'} available
                </div>
              )}
              <a
                onClick={handleLogout}
                style={{ cursor: "pointer" }}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') handleLogout();
                }}
                aria-label="Logout"
              >
                <FaSignOutAlt /> Logout
              </a>
            </>
          ) : !isActive('/login') && !isActive('/register') && (
            <>
              <Link
                to="/login"
                className={isActive('/login') ? 'active' : ''}
                aria-current={isActive('/login') ? 'page' : undefined}
              >
                <FaSignInAlt /> Login
              </Link>
              <Link
                to="/register"
                className={isActive('/register') ? 'active' : ''}
                aria-current={isActive('/register') ? 'page' : undefined}
              >
                <FaUserPlus /> Sign Up
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Mobile Menu Overlay - ONLY when menu is open */}
      {mobileMenuOpen && (
        <div 
          className="navbar-mobile-overlay" 
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}
    </nav>
  );
};

export default Navbar;